import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Camera, 
  Upload, 
  ArrowRight, 
  Loader2, 
  ImageIcon,
  Trash2,
  Sparkles,
  LogOut
} from 'lucide-react';
import Footer from '@/components/Footer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageSolverPage = () => {
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [solution, setSolution] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const { user, logout, getAuthHeaders } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('يرجى اختيار صورة بصيغة JPEG أو PNG أو WEBP');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('حجم الصورة يجب أن يكون أقل من 10 ميجابايت');
      return;
    }
    
    setImage(file);
    setSolution(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    setSolution(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const solveQuestion = async () => {
    if (!image) {
      toast.error('يرجى اختيار صورة أولاً');
      return;
    }
    
    setLoading(true);
    setSolution(null);
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        try {
          const response = await axios.post(
            `${API_URL}/api/ai/solve-image`,
            { image_base64: base64 },
            { headers: getAuthHeaders() }
          );
          
          setSolution(response.data.solution);
          toast.success('تم حل السؤال بنجاح!');
        } catch (error) {
          toast.error('فشل في حل السؤال. يرجى المحاولة مرة أخرى.');
          console.error(error);
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(image);
    } catch (error) {
      toast.error('فشل في معالجة الصورة');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] text-white">
      {/* Header */}
      <header className="bg-[#1a1a2e] border-b border-white/10" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/grades" className="flex items-center gap-3" data-testid="logo-link">
              <span className="text-xl font-bold text-white tracking-wide">
                RasheedGPT
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
            </nav>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-white/80 hover:text-white hover:bg-white/5">
                  <div className="w-8 h-8 rounded-full bg-[#4da6ff] flex items-center justify-center text-white text-sm font-bold">
                    {user?.name?.charAt(0)}
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48 bg-[#252542] border-white/10 text-white">
                <div className="px-3 py-2 border-b border-white/10">
                  <p className="font-semibold text-white">{user?.name}</p>
                  <p className="text-xs text-white/60">{user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
                </div>
                <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:text-red-300 hover:bg-white/5 cursor-pointer">
                  <LogOut className="w-4 h-4 me-2" />
                  تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/grades')}
          className="mb-6 text-white/60 hover:text-white hover:bg-white/5"
          data-testid="back-button"
        >
          <ArrowRight className="w-4 h-4 me-2" />
          العودة للصفوف
        </Button>

        {/* Page Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4da6ff]/20 rounded-full text-[#4da6ff] font-medium text-sm mb-4">
            <Camera className="w-4 h-4" />
            حل السؤال بالصورة
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            حل أي سؤال بالصورة
          </h1>
          <p className="text-lg text-white/60">
            صوّر السؤال واحصل على الحل خطوة بخطوة
          </p>
        </div>

        {/* Upload Zone */}
        <Card className="mb-8 bg-white/5 border-white/10" data-testid="upload-card">
          <CardContent className="p-6">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              className="hidden"
              data-testid="file-input"
            />
            
            {!imagePreview ? (
              <div
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                  dragOver ? 'border-[#4da6ff] bg-[#4da6ff]/10' : 'border-white/20 hover:border-white/40'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                data-testid="upload-zone"
              >
                <div className="w-20 h-20 rounded-2xl bg-[#4da6ff]/20 flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-10 h-10 text-[#4da6ff]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  ارفع صورة السؤال
                </h3>
                <p className="text-white/50 mb-4">
                  اسحب الصورة هنا أو اضغط للاختيار
                </p>
                <p className="text-sm text-white/30">
                  PNG, JPEG, WEBP (حد أقصى 10MB)
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="image-preview-container">
                <div className="relative rounded-xl overflow-hidden border border-white/10">
                  <img 
                    src={imagePreview} 
                    alt="صورة السؤال" 
                    className="w-full h-auto max-h-96 object-contain bg-black/20"
                    data-testid="image-preview"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 start-2"
                    onClick={clearImage}
                    data-testid="clear-image-btn"
                  >
                    <Trash2 className="w-4 h-4 me-1" />
                    حذف
                  </Button>
                </div>
                
                <Button
                  className="w-full bg-[#4da6ff] hover:bg-[#3d96ef] text-white py-6 text-lg font-bold"
                  onClick={solveQuestion}
                  disabled={loading}
                  data-testid="solve-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 me-2 animate-spin" />
                      جاري حل السؤال...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 me-2" />
                      حل السؤال
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Solution */}
        {solution && (
          <Card className="bg-white/5 border-white/10" data-testid="solution-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#4da6ff]" />
                الحل
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[500px]">
                <div className="prose prose-invert max-w-none whitespace-pre-wrap leading-relaxed text-white/80" data-testid="solution-content">
                  {solution}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="mt-8 bg-[#4da6ff]/10 border-[#4da6ff]/20">
          <CardContent className="p-6">
            <h3 className="font-bold text-white mb-3">
              نصائح للحصول على أفضل النتائج:
            </h3>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-start gap-2">
                <ImageIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#4da6ff]" />
                <span>تأكد من وضوح الصورة وعدم وجود ظلال</span>
              </li>
              <li className="flex items-start gap-2">
                <ImageIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#4da6ff]" />
                <span>صوّر السؤال بشكل مستقيم وليس مائلاً</span>
              </li>
              <li className="flex items-start gap-2">
                <ImageIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#4da6ff]" />
                <span>تأكد من ظهور كامل السؤال في الصورة</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default ImageSolverPage;
